import {useEffect, useRef, useState} from 'react'
import {IconX} from '@tabler/icons-react'
import {createPerson, emptyPersonForm, parseDate, type NewPersonForm} from './api/edit'
import {attachNew, type NewRole} from './api/relations'
import {fullName} from './api/tree'
import PlacePicker from './PlacePicker'
import type {TreeIndex} from './api/types'

interface Props {
  ix: TreeIndex
  /** кем создаваемый приходится тому, от кого его завели.
   *  family — в какую именно семью, если у человека их несколько */
  relation?: {role: NewRole; to: string; family?: string}
  onClose: () => void
  onCreated: (handle: string) => void
}

/** Пол и заголовок по роли. Пол подставляем, но не запираем: бывают
 *  случаи, когда в базе он записан иначе, чем подсказывает слово. */
const ROLE: Record<NewRole, {word: string; gender: number}> = {
  son: {word: 'сын', gender: 1},
  daughter: {word: 'дочь', gender: 0},
  father: {word: 'отец', gender: 1},
  mother: {word: 'мать', gender: 0},
  spouse: {word: 'супруг', gender: 2},
  brother: {word: 'брат', gender: 1},
  sister: {word: 'сестра', gender: 0},
}

const GENDERS: [number, string][] = [[1, 'Мужской'], [0, 'Женский'], [2, 'Неизвестен']]

export default function NewPersonDialog({ix, relation, onClose, onCreated}: Props) {
  const role = relation ? ROLE[relation.role] : null
  const [form, setForm] = useState<NewPersonForm>(() => {
    const f = emptyPersonForm()
    if (!role) return f
    // у супруга пол берём противоположный — это единственная роль,
    // где слово его не задаёт
    if (relation!.role === 'spouse') {
      const g = ix.people.get(relation!.to)?.gender
      return {...f, gender: g === 1 ? 0 : g === 0 ? 1 : 2}
    }
    return {...f, gender: role.gender}
  })
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  // человек создаётся раньше, чем пишется связь: если связь не легла и
  // пользователь жмёт «Создать» ещё раз, второго человека заводить нельзя
  const made = useRef<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const set = <K extends keyof NewPersonForm>(k: K, v: NewPersonForm[K]) => setForm(f => ({...f, [k]: v}))

  const badBirth = !!form.birth && !parseDate(form.birth)
  const badDeath = !!form.death && !parseDate(form.death)
  // человек без единого имени в древе бесполезен: его не найти и не отличить
  const noName = !form.surname.trim() && !form.given.trim()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (noName || badBirth || badDeath) return
    setBusy(true)
    setErr('')
    try {
      if (!made.current) made.current = await createPerson(form)
      const created = made.current
      // связь пишем сразу: человек, заведённый «сыном», без привязки
      // превращается в потерянную карточку, которую потом не найти
      if (relation) await attachNew(ix, relation.role, relation.to, created, relation.family)
      onCreated(created)
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Не удалось создать')
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <div className="mhead">
          <h2>{role ? `${form.gender === 0 ? 'Новая' : 'Новый'} ${role.word}` : 'Новый человек'}</h2>
          <button type="button" className="close" onClick={onClose} aria-label="Закрыть">
            <IconX size={18} stroke={1.8} />
          </button>
        </div>

        {relation && (
          <div className="mnote">Привяжется к: <b>{fullName(ix.people.get(relation.to))}</b></div>
        )}

        <label>
          Фамилия
          <input value={form.surname} onChange={e => set('surname', e.target.value)} autoFocus />
        </label>

        <label>
          Имя и отчество
          <input value={form.given} onChange={e => set('given', e.target.value)} />
        </label>

        <label>
          Пол
          <div className="radios">
            {GENDERS.map(([v, label]) => (
              <button type="button" key={v} className={form.gender === v ? 'on' : ''}
                      onClick={() => set('gender', v)}>
                {label}
              </button>
            ))}
          </div>
        </label>

        <div className="two">
          <label>
            Рождение
            <input value={form.birth} onChange={e => set('birth', e.target.value)}
                   placeholder="дд.мм.гггг" className={badBirth ? 'bad' : ''} />
          </label>
          <label>
            Смерть
            <input value={form.death}
                   onChange={e => setForm(f => ({...f, death: e.target.value, deceased: f.deceased || !!e.target.value.trim()}))}
                   placeholder="дд.мм.гггг" className={badDeath ? 'bad' : ''} />
          </label>
        </div>

        <label>
          Место рождения
          <PlacePicker ix={ix} value={form.birthPlace} onChange={v => set("birthPlace", v)} />
        </label>

        <label className="check">
          <input type="checkbox" checked={form.deceased} onChange={e => set('deceased', e.target.checked)} />
          <span>Человека нет в живых</span>
        </label>

        <span className="hint">
          Достаточно одного имени — остальное дозаполните потом. Сразу после создания
          откроется карточка, там фото, родство и места работы.
        </span>

        {err && <div className="err inline">{err}</div>}

        <div className="actions">
          <button type="button" className="btn" onClick={onClose}>Отмена</button>
          <button className="btn primary" disabled={busy || noName || badBirth || badDeath}>
            {busy ? 'Создаём…' : 'Создать'}
          </button>
        </div>
      </form>
    </div>
  )
}
